<script setup lang="ts">
import {Clock3, ExternalLink, Mail, MapPin, Phone, Send} from 'lucide-vue-next';
import {reactive, ref} from 'vue';
import SectionHeading from './SectionHeading.vue';
import MapPanel from './MapPanel.vue';
import {useLanguage} from '../composables/useLanguage';
import {company} from '../config/company';

const {isArabic} = useLanguage();
const form = reactive({name: '', email: '', phone: '', subject: '', message: ''});
const status = ref<'idle' | 'sending' | 'success' | 'error'>('idle');

const submit = async () => {
  status.value = 'sending';
  try {
    const apiUrl = import.meta.env.VITE_API_URL ?? '';
    const response = await fetch(`${apiUrl}/api/contact`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(form),
    });
    if (!response.ok) throw new Error('Submission failed');
    status.value = 'success';
    Object.assign(form, {name: '', email: '', phone: '', subject: '', message: ''});
  } catch {
    status.value = 'error';
  }
};
</script>

<template>
  <section id="contact" class="section contact-section">
    <div class="container">
      <SectionHeading :eyebrow="isArabic ? 'تواصل معنا' : 'Contact us'" :title="isArabic ? 'نحن هنا لخدمتكم' : 'We are here to help'" />
      <div class="contact-grid">
        <div class="contact-map reveal"><MapPanel /></div>
        <aside class="contact-details">
          <article><span><MapPin /></span><div><strong>{{ isArabic ? 'العنوان' : 'Address' }}</strong><p>{{ isArabic ? company.addressAr : company.addressEn }}</p></div></article>
          <article><span><Phone /></span><div><strong>{{ isArabic ? 'الهاتف' : 'Phone' }}</strong><a :href="`tel:${company.phone.replaceAll(' ', '')}`">{{ company.phone }}</a></div></article>
          <article><span><Mail /></span><div><strong>{{ isArabic ? 'البريد الإلكتروني' : 'Email' }}</strong><a :href="`mailto:${company.email}`">{{ company.email }}</a></div></article>
          <article><span><Clock3 /></span><div><strong>{{ isArabic ? 'ساعات العمل' : 'Working hours' }}</strong><p>{{ isArabic ? 'الأحد–الخميس، 8:00–17:00' : 'Sun–Thu, 08:00–17:00' }}</p></div></article>
          <a class="button button--ghost map-link" :href="company.googleMapsUrl" target="_blank" rel="noopener noreferrer">
            {{ isArabic ? 'فتح الموقع في Google Maps' : 'Open in Google Maps' }} <ExternalLink :size="17" />
          </a>
        </aside>
        <form class="contact-form" @submit.prevent="submit">
          <div class="form-row">
            <label><span>{{ isArabic ? 'الاسم الكامل' : 'Full name' }}</span><input v-model.trim="form.name" required /></label>
            <label><span>{{ isArabic ? 'البريد الإلكتروني' : 'Email' }}</span><input v-model.trim="form.email" type="email" required /></label>
          </div>
          <label><span>{{ isArabic ? 'رقم الهاتف' : 'Phone number' }}</span><input v-model.trim="form.phone" inputmode="tel" /></label>
          <label><span>{{ isArabic ? 'الموضوع' : 'Subject' }}</span><input v-model.trim="form.subject" required /></label>
          <label><span>{{ isArabic ? 'رسالتك' : 'Your message' }}</span><textarea v-model.trim="form.message" rows="5" required /></label>
          <button class="button button--primary button--wide" type="submit" :disabled="status === 'sending'">
            <Send :size="18" />
            {{ status === 'sending' ? (isArabic ? 'جارٍ الإرسال...' : 'Sending...') : (isArabic ? 'إرسال الرسالة' : 'Send message') }}
          </button>
          <p v-if="status === 'success'" class="form-status form-status--success">{{ isArabic ? 'تم استلام رسالتك بنجاح.' : 'Your message was received successfully.' }}</p>
          <p v-if="status === 'error'" class="form-status form-status--error">{{ isArabic ? 'تعذر الإرسال. تأكد من تشغيل خادم API.' : 'Could not send. Make sure the API server is running.' }}</p>
        </form>
      </div>
    </div>
  </section>
</template>
